/**
 * Weekly flame (superlike) budget.
 * Storage: un8.matching.flameWeek.v1
 */

import { writeLocal } from "../storage/localWrite";

const KEY = "un8.matching.flameWeek.v1";

type FlameWeek = {
  weekKey: string;
  spent: number;
  everFlamedIds: string[];
};

type MapFile = Record<string, FlameWeek>;

function weekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function readAll(): MapFile {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    return JSON.parse(raw) as MapFile;
  } catch {
    return {};
  }
}

function writeAll(map: MapFile) {
  writeLocal(KEY, JSON.stringify(map));
}

function ensure(userId: string, now = new Date()): FlameWeek {
  const map = readAll();
  const key = weekKey(now);
  let rec = map[userId];
  if (!rec) {
    rec = { weekKey: key, spent: 0, everFlamedIds: [] };
    map[userId] = rec;
    writeAll(map);
    return rec;
  }
  if (rec.weekKey !== key) {
    rec = { weekKey: key, spent: 0, everFlamedIds: rec.everFlamedIds ?? [] };
    map[userId] = rec;
    writeAll(map);
  }
  if (!Array.isArray(rec.everFlamedIds)) {
    rec.everFlamedIds = [];
    map[userId] = rec;
    writeAll(map);
  }
  return rec;
}

export const flameWeekStore = {
  remaining(userId: string, budget: number | null, now = new Date()): number | null {
    if (budget == null) return null;
    const rec = ensure(userId, now);
    return Math.max(0, budget - rec.spent);
  },

  spend(
    userId: string,
    otherId: string,
    budget: number | null,
    now = new Date()
  ): { ok: true; remaining: number | null } | { ok: false; error: "none_left" } {
    if (budget == null) return { ok: true, remaining: null };
    const map = readAll();
    const rec = ensure(userId, now);
    if (rec.everFlamedIds.includes(otherId)) {
      return { ok: true, remaining: Math.max(0, budget - rec.spent) };
    }
    if (rec.spent >= budget) return { ok: false, error: "none_left" };
    rec.spent += 1;
    rec.everFlamedIds = [...rec.everFlamedIds, otherId];
    map[userId] = rec;
    writeAll(map);
    return { ok: true, remaining: Math.max(0, budget - rec.spent) };
  },
};
